import {
  EXTERNAL_PANEL_URL_PARAM_NAME,
  type ExternalPanelKey,
  getExternalPanelBySrc,
} from "../external_panels/constants";
import { readPanelApiInterviewParam } from "./api/participantUrl";
import { is_prolific_participant } from "./prolific/utils";
import { RESPONDENT_URL_PARAMS } from "./respondent/constants";
import {
  BaseFilterSelected,
  PanelType,
  RecruitedResponsePanelType,
  Recruitment,
  RecruitmentId,
  TargetGroup,
  TargetGroupData,
  TargetGroupRestricted,
  TargetGroupWithRestricted,
} from "./types";
import { is_user_interview_participant } from "./user_interviews/utils";
import { getInterviewPath } from "@/lib/interview/links";
import { isUUID } from "@/lib/utils";
import { isObjEqual } from "@/utils/compare";
import { buildURL, notNull, uniqueByKey, isNullish } from "@/utils/helpers";
import { SearchParamsSync } from "@/utils/types";

export const getFirstCreatedTargetGroupId = <T>(
  targetGroupIds: readonly T[]
): T => {
  const targetGroupId = targetGroupIds[0];
  if (isNullish(targetGroupId)) {
    throw new Error("Target group creation returned no target groups");
  }
  return targetGroupId;
};

// A lock lease expires after this; past it the lease is treated as abandoned
// and reclaimable. Lives here (not server_queries) so both the server lock
// helpers and client read sites share one definition.
export const LOCK_TTL_MS = 15 * 60 * 1000;

export const getLeaseExpiredBefore = (nowMs: number = Date.now()) =>
  new Date(nowMs - LOCK_TTL_MS).toISOString();

// Fail closed: only a stamped lease that is provably past the TTL counts as
// expired (reclaimable); an unstamped held row is treated as locked (an
// in-flight older deployment that wrote edit_token without a timestamp).
const isLeaseActiveAt = ({
  isHeld,
  setAt,
  nowMs,
}: {
  isHeld: boolean;
  setAt: string | null;
  nowMs: number;
}): boolean => {
  if (!isHeld) {
    return false;
  }
  if (isNullish(setAt)) {
    return true;
  }
  return nowMs <= new Date(setAt).getTime() + LOCK_TTL_MS;
};

export const isLeaseActive = ({
  isHeld,
  setAt,
}: {
  isHeld: boolean;
  setAt: string | null;
}): boolean => isLeaseActiveAt({ isHeld, setAt, nowMs: Date.now() });

// The single source of truth for "is this target group locked?". Read sites must
// use this rather than `restricted.is_updating` directly.
export const isTargetGroupLockActive = (
  restricted: Pick<TargetGroupRestricted, "is_updating" | "is_updating_set_at">,
  nowMs: number = Date.now()
): boolean =>
  isLeaseActiveAt({
    isHeld: restricted.is_updating,
    setAt: restricted.is_updating_set_at,
    nowMs,
  });

// The single source of truth for "is this recruitment locked?". Read sites must
// use this rather than `recruitment.edit_token` directly.
export const isRecruitmentLockActive = (
  recruitment: Pick<Recruitment, "edit_token" | "edit_token_set_at">
): boolean =>
  isLeaseActive({
    isHeld: notNull(recruitment.edit_token),
    setAt: recruitment.edit_token_set_at,
  });

export const get_group_data = (
  group: TargetGroup
): Omit<TargetGroupData, "completion_codes"> => {
  return {
    panel: group.panel,
    filters: group.filters.sort((a, b) =>
      a.filter_id.localeCompare(b.filter_id)
    ),
    total_available_places: group.total_available_places,
    device_compatibility: uniqueByKey(
      group.device_compatibility,
      (val) => val
    ).sort(),
    respondent_panel_content_language: group.respondent_panel_content_language,
  };
};

export const some_group_changed = (groups: TargetGroupWithRestricted[]) => {
  return groups.some((g) => {
    // if the data field of the newest version is null, then it is not published yet
    if (g.restricted.data === null || g.restricted.data.filters === undefined) {
      return true;
    }
    const cur_data = get_group_data(g);
    const prev_available_places = g.restricted.data.total_available_places;
    const cur_available_places = cur_data.total_available_places;
    // it doesn't make sense to edit a group that is already (check 1) saturated (check 2)
    const progress = g.restricted.active_count + g.restricted.complete_count;
    if (
      (prev_available_places <= cur_available_places &&
        cur_available_places === progress) ||
      cur_available_places < progress
    ) {
      return false;
    }

    const get_minimal_filter = (f: BaseFilterSelected) => ({
      filter_id: f.filter_id,
      ...(f.type === "range"
        ? {
            type: f.type,
            selected_range: f.selected_range,
          }
        : {
            type: f.type,
            selected_choices: f.selected_values,
            verification_dimensions: f.verification_dimensions ?? [],
          }),
    });

    // Compare only specific filter properties
    const prevFilters = g.restricted.data.filters
      .map(get_minimal_filter)
      .filter((f) => f.type === "range" || f.selected_choices.length > 0);
    const curFilters = cur_data.filters
      .map(get_minimal_filter)
      .filter((f) => f.type === "range" || f.selected_choices.length > 0);

    return !isObjEqual(
      {
        device_compatibility: cur_data.device_compatibility,
        respondent_panel_content_language:
          cur_data.respondent_panel_content_language ?? null,
        filters: prevFilters,
        // TODO(soon): think about wether we need to compare the panel here,
        // or wether the logic for (re-)starting a recruit should take care of this
        panel: cur_data.panel,
        total_available_places: cur_data.total_available_places,
      },
      {
        device_compatibility: g.restricted.data.device_compatibility,
        respondent_panel_content_language:
          g.restricted.data.respondent_panel_content_language ?? null,
        filters: curFilters,
        panel: g.restricted.data.panel,
        total_available_places: g.restricted.data.total_available_places,
      }
    );
  });
};

export const CUSTOM_RECRUIT_PARAM = "cr__listen";

export const buildStudyLink = ({
  linkId,
  queryString,
  customRecruitId,
}: {
  linkId: string;
  queryString: string;
  customRecruitId?: string;
}): string => {
  const searchParams = new URLSearchParams(queryString);
  if (customRecruitId) {
    searchParams.set(CUSTOM_RECRUIT_PARAM, customRecruitId);
  }
  const serialized = searchParams.toString();
  return buildURL({
    path: `${getInterviewPath(linkId)}${serialized ? `?${serialized}` : ""}`,
  });
};

// If pariticipation url has this param, then we've already pre-charged for it. Distinguish
// from external self serve panel recruitments that have 'src' or other params and we bill
// / treat differently.
export const isPrePaidCustomRecruit = (
  searchParams: SearchParamsSync
): boolean => !!searchParams[CUSTOM_RECRUIT_PARAM];

export const getCustomRecruitmentId = (
  searchParams: SearchParamsSync
): RecruitmentId | null => {
  // The `cr__listen` param value is the custom-link recruitment id.
  const val = searchParams[CUSTOM_RECRUIT_PARAM];
  return typeof val === "string" && isUUID(val) ? (val as RecruitmentId) : null;
};

// TODO(future) custom recruit detection might not be solely determined by the src parameter
export const isCustomRecruit = (searchParams: SearchParamsSync): boolean => {
  return !!searchParams[EXTERNAL_PANEL_URL_PARAM_NAME];
};

export const isRecruitedParticipant = (
  searchParams: SearchParamsSync
): boolean => {
  return (
    notNull(get_panel_from_search_params(searchParams)) ||
    isCustomRecruit(searchParams) ||
    isPrePaidCustomRecruit(searchParams)
  );
};

// The URL params can't distinguish the two respondent panels (see
// get_panel_from_search_params); consumers holding the target group should
// prefer its panel when both sides pass this check.
export const isRespondentPanel = (
  panel: PanelType
): panel is "respondent_b2c" | "respondent_b2b" =>
  panel === "respondent_b2c" || panel === "respondent_b2b";

// TODO(soon): detect a Panel API arrival by its interview id
export const get_panel_from_search_params = (
  searchParams: SearchParamsSync
): Exclude<PanelType, "panel_api"> | null => {
  if (is_prolific_participant(searchParams)) {
    return "prolific" as const;
  }
  if (is_user_interview_participant(searchParams)) {
    return "user_interviews" as const;
  }
  if (isRespondentParticipant(searchParams)) {
    // The participant URL doesn't carry the b2c/b2b discriminator (only
    // the project id does), so we return _b2c arbitrarily here. Never
    // strict-compare this against a target group's panel; check both sides
    // with isRespondentPanel instead.
    // TODO(future): once link_ids become unique per target group, look
    // the panel up from the target group keyed by link_id and return
    // the actual b2c/b2b value.
    return "respondent_b2c" as const;
  }
  return null;
};

export type RecruitmentPanelKey =
  | "prolific"
  | "user_interviews"
  | "respondent"
  | "panel_api"
  | ExternalPanelKey;

// Null when self-recruited or the source is not a panel we know.
export const getRecruitmentPanel = ({
  searchParams,
}: {
  searchParams: SearchParamsSync;
}): RecruitmentPanelKey | null => {
  if (is_prolific_participant(searchParams)) return "prolific";
  if (is_user_interview_participant(searchParams)) return "user_interviews";
  if (isRespondentParticipant(searchParams)) return "respondent";
  if (readPanelApiInterviewParam(searchParams).kind === "present") {
    return "panel_api";
  }
  const src = searchParams[EXTERNAL_PANEL_URL_PARAM_NAME];
  return typeof src === "string"
    ? (getExternalPanelBySrc(src)?.key ?? null)
    : null;
};

// A listed src counts only when no panel claims the link, so Prolific or a vendor can't opt in through it.
export const isEndPageAutoContinueOn = ({
  panel,
  src,
  enabledPanels,
  enabledSrcs = [],
}: {
  panel: RecruitmentPanelKey | null | undefined;
  src: string | undefined;
  enabledPanels: readonly ExternalPanelKey[];
  enabledSrcs?: readonly string[];
}): boolean => {
  const isPanelListed = enabledPanels.some(
    (enabledPanel) => enabledPanel === panel
  );
  const isSrcListed =
    panel === null && enabledSrcs.some((enabledSrc) => enabledSrc === src);
  return isPanelListed || isSrcListed;
};

export const getRecruitedResponsePanelFromSearchParams = ({
  searchParams,
}: {
  searchParams: SearchParamsSync;
}): RecruitedResponsePanelType | null => {
  return isCustomRecruit(searchParams) || isPrePaidCustomRecruit(searchParams)
    ? "custom"
    : get_panel_from_search_params(searchParams);
};

// Respondent appends all four ids to every invite; a partial set is some other recruit.
// Our Respondent links never carry src, so one that does is a link recruit.
export const isRespondentParticipant = (
  searchParams: SearchParamsSync
): boolean =>
  !isCustomRecruit(searchParams) &&
  RESPONDENT_URL_PARAMS.every(
    (param) => typeof searchParams?.[param] === "string"
  );

/**
 * returns an incidence rate for a target group. Make sure to pass in the aggregated
 * return/complete counts over all revisions (which you get from querying with get_target_group...)
 *
 * @returns - undefined if the group is unpublished
 *          - 1 if there are no finished submissions yet
 *          - else #complete / (#returned + #complete)
 *
 * For prolific studies we have: #returned = submissions that are screened out,
 * returned or where we requested them to return
 */
export const get_incidence_rate = (group: TargetGroupRestricted) => {
  const num_finished_submissions = group.screened_count + group.complete_count;
  const incidence_rate = !group.data
    ? undefined
    : num_finished_submissions === 0
      ? 1
      : Math.min(1, group.complete_count / num_finished_submissions);
  return incidence_rate;
};

// returns the difference in target number of responses between current version and currently published version
export const get_responses_to_count_delta = (
  group: TargetGroupWithRestricted
) => {
  return (
    group.total_available_places -
    (group.restricted.data?.total_available_places ?? 0)
  );
};
